package com.example.readingnotes.presentation.admin

import com.example.readingnotes.domain.repository.PostRepository
import com.example.readingnotes.domain.usecase.CreatePostUseCase
import com.example.readingnotes.domain.usecase.DeletePostUseCase
import com.example.readingnotes.domain.usecase.EditImageUseCase
import com.example.readingnotes.domain.usecase.UpdatePostUseCase
import com.example.readingnotes.domain.usecase.UploadImageUseCase
import com.example.readingnotes.util.ErrorMessageProvider
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AdminPostEditorViewModel(
    private val createPostUseCase: CreatePostUseCase,
    private val viewModelScope: CoroutineScope,
    private val postRepository: PostRepository? = null,
    private val updatePostUseCase: UpdatePostUseCase? = null,
    private val deletePostUseCase: DeletePostUseCase? = null,
    private val uploadImageUseCase: UploadImageUseCase? = null,
    private val editImageUseCase: EditImageUseCase? = null,
) {
    private val _state = MutableStateFlow(AdminPostEditorState())
    val state: StateFlow<AdminPostEditorState> = _state.asStateFlow()

    fun onEvent(event: AdminPostEditorEvent) {
        when (event) {
            is AdminPostEditorEvent.OnTitleChanged -> {
                _state.update { it.copy(titleInput = event.text, errorMessage = null) }
            }
            is AdminPostEditorEvent.OnContentChanged -> {
                _state.update { it.copy(contentInput = event.text, errorMessage = null) }
            }
            is AdminPostEditorEvent.OnImageUrlChanged -> {
                _state.update { it.copy(imageUrlInput = event.text) }
            }
            is AdminPostEditorEvent.OnStatusChanged -> {
                _state.update { it.copy(statusInput = event.status) }
            }
            is AdminPostEditorEvent.OnSubmit -> submit()
            is AdminPostEditorEvent.OnReset -> reset()
            is AdminPostEditorEvent.OnLoadPost -> loadPost(event.id)
            is AdminPostEditorEvent.OnUpdatePost -> updatePost(event.id)
            is AdminPostEditorEvent.OnDeletePost -> showDeleteConfirmation(event.id)
            is AdminPostEditorEvent.OnChangeStatus -> changeStatus(event.id, event.status)
            is AdminPostEditorEvent.OnSelectImage -> {
                // This should trigger platform-specific image picker
                // The result will come back as OnImageSelected
            }
            is AdminPostEditorEvent.OnImageSelected -> {
                uploadImage(event.fileName, event.mimeType, event.imageData)
            }
            is AdminPostEditorEvent.OnUploadImage -> {
                uploadImage(event.fileName, event.mimeType, event.imageData)
            }
            is AdminPostEditorEvent.OnImagePreview -> {
                _state.update { it.copy(tempImageUrl = event.url) }
            }
            is AdminPostEditorEvent.OnImageUploadError -> {
                _state.update { it.copy(imageUploadError = event.message) }
            }
            is AdminPostEditorEvent.OnRemoveImage -> {
                removeImage(event.imageId)
            }
            is AdminPostEditorEvent.OnEditImage -> {
                startEditingImage(event.imageId)
            }
            is AdminPostEditorEvent.OnTotpCodeChanged -> {
                _state.update { it.copy(totpCodeInput = event.code, errorMessage = null) }
            }
            is AdminPostEditorEvent.OnConfirmDelete -> confirmDelete()
            is AdminPostEditorEvent.OnCancelDelete -> cancelDelete()
        }
    }

    private fun loadPost(id: String) {
        if (postRepository == null) return

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            postRepository
                .getPost(id)
                .onSuccess { post ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isEditMode = true,
                            editingPostId = id,
                            originalPost = post,
                            titleInput = post.title,
                            contentInput = post.contentMarkdown,
                            imageUrlInput = post.imageUrl ?: "",
                            statusInput = post.status ?: "published",
                            images = post.images ?: emptyList(),
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "投稿の読み込みに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun updatePost(id: String) {
        if (updatePostUseCase == null) {
            _state.update { it.copy(errorMessage = "更新機能は利用できません") }
            return
        }

        val title = _state.value.titleInput
        val content = _state.value.contentInput
        val imageUrl = _state.value.imageUrlInput.takeIf { it.isNotBlank() }
        val status = _state.value.statusInput
        val totpCode = _state.value.totpCodeInput.takeIf { it.isNotBlank() }
        val imageIds = _state.value.images.map { it.id }

        if (title.isBlank()) {
            _state.update { it.copy(errorMessage = "タイトルを入力してください") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isSubmitting = true, errorMessage = null) }

            updatePostUseCase(
                id = id,
                title = title,
                contentMarkdown = content,
                imageUrl = imageUrl,
                imageIds = imageIds,
                status = status,
                totpCode = totpCode,
            ).onSuccess {
                _state.update {
                    it.copy(
                        isSubmitting = false,
                        isSuccess = true,
                        totpCodeInput = "",
                    )
                }
            }.onFailure { error ->
                val isTotpRequired = error.message?.contains("TOTP") == true
                _state.update {
                    it.copy(
                        isSubmitting = false,
                        totpRequired = isTotpRequired,
                        errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "更新に失敗しました"),
                    )
                }
            }
        }
    }

    private fun showDeleteConfirmation(id: String) {
        _state.update {
            it.copy(
                showDeleteConfirmation = true,
                deletingPostId = id,
            )
        }
    }

    private fun confirmDelete() {
        val id = _state.value.deletingPostId ?: return
        if (deletePostUseCase == null) {
            _state.update { it.copy(errorMessage = "削除機能は利用できません") }
            return
        }

        val totpCode = _state.value.totpCodeInput.takeIf { it.isNotBlank() }

        viewModelScope.launch {
            _state.update { it.copy(isDeleting = true, errorMessage = null) }

            deletePostUseCase(id, totpCode)
                .onSuccess {
                    _state.update {
                        it.copy(
                            isDeleting = false,
                            deleteSuccess = true,
                            showDeleteConfirmation = false,
                            deletingPostId = null,
                            totpCodeInput = "",
                        )
                    }
                }.onFailure { error ->
                    val isTotpRequired = error.message?.contains("TOTP") == true
                    _state.update {
                        it.copy(
                            isDeleting = false,
                            totpRequired = isTotpRequired,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "削除に失敗しました"),
                        )
                    }
                }
        }
    }

    private fun cancelDelete() {
        _state.update {
            it.copy(
                showDeleteConfirmation = false,
                deletingPostId = null,
                totpCodeInput = "",
            )
        }
    }

    private fun changeStatus(id: String, status: String) {
        if (postRepository == null) return

        val totpCode = _state.value.totpCodeInput.takeIf { it.isNotBlank() }

        viewModelScope.launch {
            _state.update { it.copy(isSubmitting = true, errorMessage = null) }

            postRepository
                .updatePostStatus(id, status, totpCode)
                .onSuccess {
                    _state.update {
                        it.copy(
                            isSubmitting = false,
                            isSuccess = true,
                            statusInput = status,
                            totpCodeInput = "",
                        )
                    }
                }.onFailure { error ->
                    val isTotpRequired = error.message?.contains("TOTP") == true
                    _state.update {
                        it.copy(
                            isSubmitting = false,
                            totpRequired = isTotpRequired,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "ステータス変更に失敗しました"),
                        )
                    }
                }
        }
    }

    private fun submit() {
        val title = _state.value.titleInput
        val content = _state.value.contentInput
        val imageUrl = _state.value.imageUrlInput.takeIf { it.isNotBlank() }
        val imageIds = _state.value.images.map { it.id }

        if (title.isBlank()) {
            _state.update { it.copy(errorMessage = "タイトルを入力してください") }
            return
        }
        if (content.isBlank()) {
            _state.update { it.copy(errorMessage = "本文を入力してください") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isSubmitting = true, errorMessage = null) }

            // Use imageIds if available, fallback to imageUrl for backward compatibility
            createPostUseCase(
                title = title,
                contentMarkdown = content,
                imageUrl = imageUrl,
                imageIds = imageIds,
            ).onSuccess {
                _state.update {
                    it.copy(
                        isSubmitting = false,
                        isSuccess = true,
                        titleInput = "",
                        contentInput = "",
                        imageUrlInput = "",
                        images = emptyList(),
                    )
                }
            }.onFailure { error ->
                _state.update {
                    it.copy(
                        isSubmitting = false,
                        errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "投稿に失敗しました"),
                    )
                }
            }
        }
    }

    private fun reset() {
        _state.update {
            AdminPostEditorState().copy(tempImageUrl = null)
        }
    }

    private fun uploadImage(fileName: String, mimeType: String, imageData: ByteArray) {
        if (uploadImageUseCase == null) {
            _state.update {
                it.copy(
                    imageUploadError = "画像アップロード機能は利用できません",
                )
            }
            return
        }

        if (_state.value.images.size >= 5) {
            _state.update {
                it.copy(
                    imageUploadError = "最大5枚まで画像をアップロードできます",
                )
            }
            return
        }

        viewModelScope.launch {
            _state.update {
                it.copy(
                    isUploadingImage = true,
                    imageUploadError = null,
                )
            }

            uploadImageUseCase(fileName, mimeType, imageData)
                .onSuccess { image ->
                    _state.update {
                        it.copy(
                            isUploadingImage = false,
                            images = it.images + image,
                            imageUrlInput = image.url, // Keep backward compatibility
                            tempImageUrl = null, // Clear temp preview
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isUploadingImage = false,
                            imageUploadError =
                                ErrorMessageProvider.getLocalizedMessage(
                                    error,
                                    "画像のアップロードに失敗しました",
                                ),
                        )
                    }
                }
        }
    }

    private fun removeImage(imageId: String) {
        _state.update {
            it.copy(
                images = it.images.filter { it.id != imageId },
                imageUrlInput = if (it.images.size <= 1) "" else it.imageUrlInput,
            )
        }
    }

    private fun startEditingImage(imageId: String) {
        val image = _state.value.images.find { it.id == imageId }
        if (image != null) {
            _state.update {
                it.copy(
                    editingImage = image,
                    isEditingImage = true,
                    imageEditError = null,
                )
            }
        }
    }

    fun editImage(
        imageId: String,
        caption: String? = null,
        altText: String? = null,
        rotation: Float? = null,
        cropParams: com.example.readingnotes.domain.usecase.CropParams? = null,
    ) {
        if (editImageUseCase == null) return

        viewModelScope.launch {
            _state.update { it.copy(isEditingImage = true, imageEditError = null) }

            editImageUseCase(imageId, null, caption, altText, rotation, cropParams)
                .onSuccess { updatedImage ->
                    _state.update { currentState ->
                        currentState.copy(
                            isEditingImage = false,
                            editingImage = null,
                            images =
                                currentState.images.map {
                                    if (it.id == imageId) updatedImage else it
                                },
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isEditingImage = false,
                            imageEditError = error.message ?: "画像の編集に失敗しました",
                        )
                    }
                }
        }
    }

    fun cancelImageEdit() {
        _state.update {
            it.copy(
                editingImage = null,
                isEditingImage = false,
                imageEditError = null,
            )
        }
    }

    fun reorderImages(fromIndex: Int, toIndex: Int) {
        _state.update { currentState ->
            val reorderedImages = currentState.images.toMutableList()
            val movedImage = reorderedImages.removeAt(fromIndex)
            reorderedImages.add(toIndex, movedImage)
            currentState.copy(images = reorderedImages)
        }
    }
}
